/**
 * Fixed color palette for visualizations
 * These colors are designed to work well together and remain consistent
 * regardless of the app's light/dark theme.
 */

// Base palette - vibrant, accessible colors
export const vizColors = {
  // Primary palette
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
  },
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
  },
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  rose: {
    50: '#fff1f2',
    100: '#ffe4e6',
    200: '#fecdd3',
    300: '#fda4af',
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',
  },
  emerald: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
  },
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

// Timeline specific colors
export const timelineColors = {
  background: '#ffffff',
  cardBackground: '#f8fafc',
  border: '#e2e8f0',
  text: {
    primary: '#1e293b',
    secondary: '#64748b',
    muted: '#94a3b8',
  },
  line: {
    gradient: ['#3b82f6', '#8b5cf6', '#d946ef'],
  },
  importance: {
    milestone: {
      bg: '#fef3c7',
      border: '#f59e0b',
      text: '#b45309',
      dot: '#f59e0b',
    },
    major: {
      bg: '#dbeafe',
      border: '#3b82f6',
      text: '#1d4ed8',
      dot: '#3b82f6',
    },
    minor: {
      bg: '#f1f5f9',
      border: '#cbd5e1',
      text: '#475569',
      dot: '#94a3b8',
    },
  },
};

// Flowchart colors
export const flowchartColors = {
  background: '#ffffff',
  nodeTypes: {
    start: { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
    end: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
    process: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
    decision: { bg: '#fef3c7', border: '#f59e0b', text: '#b45309' },
    input: { bg: '#f3e8ff', border: '#a855f7', text: '#7c3aed' },
    output: { bg: '#ccfbf1', border: '#14b8a6', text: '#0f766e' },
  },
  edge: {
    line: '#94a3b8',
    arrow: '#64748b',
    label: '#475569',
  },
};

// Concept map colors
export const conceptMapColors = {
  background: '#ffffff',
  central: { bg: '#3b82f6', text: '#ffffff', border: '#2563eb' },
  level1: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  level2: { bg: '#f3e8ff', text: '#7c3aed', border: '#d8b4fe' },
  level3: { bg: '#ccfbf1', text: '#0f766e', border: '#5eead4' },
  connection: { line: '#cbd5e1', label: '#64748b' },
};

// Cycle diagram colors
export const cycleColors = {
  background: '#ffffff',
  steps: [
    { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
    { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
    { bg: '#fef3c7', border: '#f59e0b', text: '#b45309' },
    { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
    { bg: '#f3e8ff', border: '#a855f7', text: '#7c3aed' },
    { bg: '#ccfbf1', border: '#14b8a6', text: '#0f766e' },
  ],
  arrow: '#94a3b8',
  center: { bg: '#f8fafc', border: '#e2e8f0' },
};

// Hierarchy colors
export const hierarchyColors = {
  background: '#ffffff',
  levels: [
    { bg: '#3b82f6', text: '#ffffff', border: '#2563eb' },
    { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    { bg: '#eff6ff', text: '#3b82f6', border: '#bfdbfe' },
    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' },
  ],
  connector: '#cbd5e1',
};

// Graph/chart colors
export const chartColors = {
  background: '#ffffff',
  grid: '#f1f5f9',
  axis: '#cbd5e1',
  axisLabel: '#64748b',
  series: [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
  ],
};

// Comparison table colors
export const tableColors = {
  background: '#ffffff',
  header: { bg: '#f1f5f9', text: '#1e293b', border: '#e2e8f0' },
  row: { bg: '#ffffff', altBg: '#f8fafc', text: '#334155', border: '#e2e8f0' },
  highlight: { bg: '#dbeafe', text: '#1d4ed8' },
};

// Modal/overlay colors (for detail views within visualizations)
export const overlayColors = {
  backdrop: 'rgba(15, 23, 42, 0.7)', // slate-900 with opacity
  card: {
    background: '#ffffff',
    border: '#e2e8f0',
    shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  text: {
    primary: '#1e293b',
    secondary: '#64748b',
  },
};
